import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import ThemeToggle from "@/components/feature/ThemeToggle";
import { useIsDarkPage, getThemedHref } from "@/hooks/useThemedHref";
import { navigation } from "@/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isDark = useIsDarkPage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = navigation.booker.links;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 dark:bg-[#0C0C0E]/90 backdrop-blur-xl shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a href={getThemedHref("/", isDark)} className="flex items-center gap-2 cursor-pointer group">
          <div className={`w-7 h-7 flex items-center justify-center rounded-md transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ${
            scrolled
              ? "bg-amber-600 dark:bg-amber-500 group-hover:bg-amber-500"
              : "bg-amber-500 group-hover:bg-amber-400"
          }`}>
            <i className={`ri-flashlight-line text-sm transition-transform duration-300 group-hover:scale-110 ${
              scrolled ? "text-white dark:text-[#0C0C0E]" : "text-[#0A0A0A]"
            }`}></i>
          </div>
          <span className={`text-lg font-bold tracking-tight transition-colors duration-300 ${
            scrolled
              ? "text-gray-900 dark:text-white group-hover:text-amber-700 dark:group-hover:text-amber-300"
              : "text-white group-hover:text-amber-300"
          }`}>
            Быстренко<span className={`transition-colors duration-300 ${
              scrolled
                ? "text-amber-600 dark:text-amber-500 group-hover:text-amber-700 dark:group-hover:text-amber-300"
                : "text-amber-600 group-hover:text-amber-300"
            }`}>.ру</span>
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled
                  ? "text-gray-700 hover:text-gray-900 dark:text-white/60 dark:hover:text-white"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* CTA + Theme */}
        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <a
            href="#contact"
            className="text-sm font-semibold px-5 py-2.5 rounded-full bg-gradient-to-r from-yellow-400 via-red-500 to-pink-500 text-white shadow-lg hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
          >
            Попробовать бесплатно
          </a>
        </div>

        {/* Mobile burger */}
        <button
          className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl ${
            scrolled ? "text-gray-800 dark:text-white" : "text-white"
          }`}></i>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white/95 dark:bg-[#0C0C0E]/95 backdrop-blur-xl border-t border-gray-100 dark:border-white/[0.07] px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="text-gray-700 dark:text-slate-300 dark:hover:text-white font-medium text-sm cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <ThemeToggle />
            <span className="text-gray-500 dark:text-slate-400 text-xs">{navigation.booker.themeLabel}</span>
          </div>
          <a
            href="#contact"
            onClick={() => setMenuOpen(false)}
            className="mt-2 text-center text-sm font-semibold px-5 py-2.5 rounded-full bg-gradient-to-r from-yellow-400 via-red-500 to-pink-500 text-white cursor-pointer whitespace-nowrap shadow-lg"
          >
            Попробовать бесплатно
          </a>
        </div>
      )}
    </nav>
  );
}
